import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  BookOpen, Check, ChevronDown, ExternalLink, FolderInput, Loader2, Target, Undo2,
} from "lucide-react";

const KIND_LABEL = { resource: "Material", link: "Enlace", practice: "Práctica", quiz: "Quiz" };

/** El plan de estudio de UNA clase: qué se dio, qué sigue y el material de cada unidad. */
export const GroupPlan = ({ groupId, canMark = false }) => {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [abierta, setAbierta] = useState(null);
  const [ocupado, setOcupado] = useState("");

  const load = useCallback(() => {
    if (!groupId) return;
    setCargando(true);
    api.get("/my-plan", { params: { group_id: groupId } })
      .then((r) => {
        setData(r.data);
        setAbierta(r.data?.current_unit_id || null);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo abrir el plan"))
      .finally(() => setCargando(false));
  }, [groupId]);
  useEffect(() => { load(); }, [load]);

  const marcar = async (unit, hecho) => {
    setOcupado(unit.id);
    try {
      const r = hecho
        ? await api.post(`/my-plan/units/${unit.id}/done`, { group_id: groupId })
        : await api.delete(`/my-plan/units/${unit.id}/done`, { params: { group_id: groupId } });
      setData(r.data);
      setAbierta(r.data?.current_unit_id || null);
      toast.success(hecho ? "Unidad marcada como vista" : "Se quitó la marca");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setOcupado(""); }
  };

  const traerALibreta = async (unit) => {
    const ids = [...(unit.materials || []), ...(unit.practices || [])]
      .filter((m) => m.resource_id).map((m) => m.resource_id);
    if (!ids.length) { toast.error("Esta unidad no tiene material de la biblioteca"); return; }
    setOcupado(unit.id);
    try {
      for (const rid of ids) {
        await api.post("/notebook/from-resource", { resource_id: rid, group_id: groupId });
      }
      toast.success(`${ids.length} archivo(s) en la libreta de la clase`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo archivar");
    } finally { setOcupado(""); }
  };

  if (cargando) {
    return <Card className="p-10 text-center text-slate-500" data-testid="group-plan-loading">
      <Loader2 className="h-5 w-5 animate-spin mx-auto" /></Card>;
  }
  if (!data?.plan) {
    return (
      <Card className="p-8 text-center" data-testid="group-plan-empty">
        <BookOpen className="h-7 w-7 text-slate-300 mx-auto mb-3" />
        <p className="text-sm text-slate-600 font-medium">Esta clase todavía no tiene plan de estudio</p>
        <p className="text-xs text-slate-500 mt-1.5 max-w-sm mx-auto">
          {data?.level
            ? `Está marcada como nivel ${data.level}, pero ese nivel no tiene un plan creado.`
            : "Falta marcarle el nivel a la clase en Programas y clases."}
        </p>
      </Card>
    );
  }

  const pct = data.units_total ? Math.round((data.units_done / data.units_total) * 100) : 0;

  return (
    <div data-testid="group-plan">
      <div className="rounded-2xl bg-[#0d1b2a] text-white p-6 mb-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <h3 className="font-semibold text-lg tracking-tight">{data.group_name}</h3>
            <p className="text-[12.5px] text-slate-400 mt-0.5">{data.plan.label}</p>
          </div>
          {data.plan.source_doc_url && (
            <a href={data.plan.source_doc_url} target="_blank" rel="noreferrer"
               className="text-[12px] font-semibold text-slate-300 hover:text-white inline-flex items-center gap-1.5"
               data-testid="plan-source-doc">
              <ExternalLink className="h-3.5 w-3.5" /> Documento del plan
            </a>
          )}
        </div>
        <div className="h-[7px] rounded-full bg-white/15 overflow-hidden mt-5">
          <div className="h-full rounded-full bg-gradient-to-r from-[#ff731d] to-[#ff9f5a] transition-all duration-500"
               style={{ width: `${pct}%` }} data-testid="plan-progress-bar" />
        </div>
        <div className="flex gap-8 mt-4 flex-wrap text-[13px]">
          <div><span className="block text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Unidades</span>
            <b data-testid="plan-units-count">{data.units_done} de {data.units_total}</b></div>
          <div><span className="block text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Clases</span>
            <b>{data.classes_done} de {data.classes_total}</b></div>
        </div>
      </div>

      {data.units.map((u, i) => {
        const esActual = u.id === data.current_unit_id;
        const open = abierta === u.id;
        return (
          <Card key={u.id}
                className={`mb-2.5 overflow-hidden transition-shadow ${esActual ? "border-[#ff731d] shadow-[0_6px_22px_rgba(255,115,29,0.13)]" : ""}`}
                data-testid={`plan-unit-${u.id}`}>
            <button type="button" onClick={() => setAbierta(open ? null : u.id)}
                    className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-slate-50 transition-colors"
                    data-testid={`plan-unit-toggle-${u.id}`}>
              <span className={`h-7 w-7 rounded-lg grid place-items-center text-[12px] font-extrabold shrink-0
                ${u.done ? "bg-emerald-100 text-emerald-700" : esActual ? "bg-[#ff731d] text-white" : "bg-slate-100 text-slate-500"}`}>
                {u.done ? <Check className="h-3.5 w-3.5" /> : i + 1}
              </span>
              <span className="flex-1 min-w-0 text-sm font-semibold text-slate-900">{u.title}</span>
              {u.done ? (
                <span className="text-[11px] font-bold rounded-full px-2.5 py-1 bg-emerald-50 text-emerald-700 shrink-0"
                      data-testid={`plan-unit-done-${u.id}`}>Visto · {u.done_on}</span>
              ) : esActual ? (
                <span className="text-[11px] font-bold rounded-full px-2.5 py-1 bg-orange-50 text-orange-700 shrink-0">Van aquí</span>
              ) : null}
              <span className="text-[11px] text-slate-400 shrink-0 hidden sm:inline">
                {u.class_count} {u.class_count === 1 ? "clase" : "clases"}
              </span>
              <ChevronDown className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>

            {open && (
              <div className="px-4 pb-4 pt-3 border-t border-slate-100 sm:pl-14">
                {!!u.objectives?.length && (
                  <div className="mb-3.5">
                    <h5 className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-bold mb-1.5 flex items-center gap-1.5">
                      <Target className="h-3 w-3" /> Al terminar, el estudiante puede
                    </h5>
                    <ul className="list-disc pl-4 text-[13px] text-slate-700 leading-7">
                      {u.objectives.map((o, k) => <li key={k}>{o}</li>)}
                    </ul>
                  </div>
                )}
                {!!u.grammar?.length && (
                  <div className="mb-3.5">
                    <h5 className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-bold mb-1.5">Gramática</h5>
                    <ul className="list-disc pl-4 text-[13px] text-slate-700 leading-7">
                      {u.grammar.map((o, k) => <li key={k}>{o}</li>)}
                    </ul>
                  </div>
                )}
                {u.vocab && (
                  <div className="mb-3.5">
                    <h5 className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-bold mb-1.5">Vocabulario</h5>
                    <p className="text-[13px] text-slate-700">{u.vocab}</p>
                  </div>
                )}
                <MaterialList titulo="Materiales de la unidad" items={u.materials} unitId={u.id} />
                <MaterialList titulo="Prácticas" items={u.practices} unitId={u.id} />

                {canMark && (
                  <div className="flex flex-wrap gap-2 pt-1.5">
                    <Button size="sm" variant="outline" disabled={ocupado === u.id}
                            onClick={() => traerALibreta(u)} data-testid={`plan-unit-tolibreta-${u.id}`}>
                      <FolderInput className="h-3.5 w-3.5" /> Traer material a la libreta
                    </Button>
                    {u.done ? (
                      <Button size="sm" variant="ghost" disabled={ocupado === u.id}
                              onClick={() => marcar(u, false)} data-testid={`plan-unit-unmark-${u.id}`}>
                        <Undo2 className="h-3.5 w-3.5" /> Quitar la marca
                      </Button>
                    ) : (
                      <Button size="sm" className="btn-brand text-white" disabled={ocupado === u.id}
                              onClick={() => marcar(u, true)} data-testid={`plan-unit-mark-${u.id}`}>
                        <Check className="h-3.5 w-3.5" /> Marcar como vista
                      </Button>
                    )}
                    {u.done_by_name && (
                      <span className="text-[11px] text-slate-400 self-center">Marcada por {u.done_by_name}</span>
                    )}
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
};

const MaterialList = ({ titulo, items, unitId }) => {
  if (!items?.length) return null;
  return (
    <div className="mb-3.5">
      <h5 className="text-[10.5px] uppercase tracking-[0.14em] text-slate-400 font-bold mb-1.5">{titulo}</h5>
      <div className="flex flex-col gap-1.5">
        {items.map((m, k) => {
          const href = m.resource_id ? `/dashboard/resources?r=${m.resource_id}` : m.url;
          return (
            <a key={k} href={href || "#"} target={m.resource_id ? "_self" : "_blank"} rel="noreferrer"
               className="flex items-center gap-2.5 text-[13px] border border-slate-200 rounded-lg px-3 py-2 hover:border-slate-300 transition-colors"
               data-testid={`plan-material-${unitId}-${k}`}>
              <span className="flex-1 min-w-0 truncate text-slate-800">{m.name}</span>
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0">
                {KIND_LABEL[m.kind] || m.kind}
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

export default GroupPlan;
